/** Server-side cap on a single bulk-import request's row count (see
 *  `importCustomersSchema`/`importProductsSchema`) — kept here so the client
 *  chunker and the server schemas can't drift apart. */
export const IMPORT_MAX_ROWS_PER_REQUEST = 500;
/** Byte budget for one chunked request's `rows` payload — comfortably under
 *  the API's 1 MiB `bodyLimit`, leaving room for the rest of the JSON body
 *  and HTTP overhead. */
export const IMPORT_MAX_BYTES_PER_REQUEST = 700_000;
/** UTF-8 byte length of `JSON.stringify(value)` — the unit the server's body
 *  limit actually counts in, not JS string length. Works in both the browser
 *  and Node. */
export function jsonByteLength(value) {
    return new TextEncoder().encode(JSON.stringify(value)).length;
}
/**
 * Splits `items` into ordered chunks, starting a new chunk whenever adding
 * the next item would exceed `maxRows` or `maxBytes`. A single item whose own
 * size already exceeds `maxBytes` still gets a chunk of its own — never an
 * empty chunk, and the loop always makes progress.
 *
 * Concatenating the returned chunks reproduces `items` exactly; this is a
 * pure split, not a reorder.
 */
export function chunkForImport(items, byteSizeOf, options) {
    const maxRows = options?.maxRows ?? IMPORT_MAX_ROWS_PER_REQUEST;
    const maxBytes = options?.maxBytes ?? IMPORT_MAX_BYTES_PER_REQUEST;
    const chunks = [];
    let current = [];
    let currentBytes = 0;
    for (const item of items) {
        const itemBytes = byteSizeOf(item);
        const wouldOverflow = current.length > 0 && (current.length >= maxRows || currentBytes + itemBytes > maxBytes);
        if (wouldOverflow) {
            chunks.push(current);
            current = [];
            currentBytes = 0;
        }
        current.push(item);
        currentBytes += itemBytes;
    }
    if (current.length > 0)
        chunks.push(current);
    return chunks;
}
/**
 * Stable sort by how deep each item sits in the parent chain described by the
 * file itself: items with no parent (or a parent that is not in the file) come
 * first, then their children, then grandchildren. Used so a product import's
 * variation rows never land in an earlier chunk than the parent row they
 * depend on, even when the parent appears later in the source file and the
 * tree is more than one level deep. A parent loop (which the server reports)
 * is parked at the end rather than looping forever.
 */
export function depthOrdered(items, keyOf, parentKeyOf) {
    const parentByKey = new Map();
    for (const item of items) {
        const key = keyOf(item);
        if (key)
            parentByKey.set(key, parentKeyOf(item));
    }
    const MAX_WALK = 4;
    const depthOf = (item) => {
        let depth = 0;
        let parent = parentKeyOf(item);
        while (parent && parentByKey.has(parent)) {
            depth++;
            if (depth > MAX_WALK)
                return MAX_WALK + 1;
            parent = parentByKey.get(parent) ?? null;
        }
        return parent ? depth + 1 : depth;
    };
    return items
        .map((item, index) => ({ item, index, depth: depthOf(item) }))
        .sort((a, b) => a.depth - b.depth || a.index - b.index)
        .map((e) => e.item);
}
//# sourceMappingURL=import-chunking.js.map